import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../components/base/SEO";
import Footer from "../../components/feature/Footer";
import Header from "../../components/feature/Header";

const solutions = [
  {
    title: "電商物流 WMS",
    titleEn: "E-commerce WMS",
    eyebrow: "網店 / 多平台履行",
    eyebrowEn: "Online stores / multi-channel fulfillment",
    description:
      "把商品同步、入庫分配、庫存聯動、揀貨包裝與出貨回寫放在同一條流程。",
    descriptionEn:
      "Connect product sync, inbound allocation, inventory, pick-pack, and shipment updates in one workflow.",
    href: "/wms/ecommerce",
    cta: "查看電商 WMS",
    ctaEn: "View E-commerce WMS",
    icon: "ri-shopping-bag-3-line",
    image: "/ecommerce-hero-visual.png",
    accent: "text-teal-600",
    bg: "bg-teal-50",
  },
  {
    title: "第三方物流 WMS",
    titleEn: "3PL WMS",
    eyebrow: "多貨主 / 多 SOP / 多收費",
    eyebrowEn: "Multi-client / SOP / billing",
    description:
      "專為 3PL 倉設計，處理貨主管理、入出庫作業、增值服務、計費對帳與客戶查詢。",
    descriptionEn:
      "Designed for 3PL warehouses handling clients, warehouse tasks, value-added services, billing, and portals.",
    href: "/wms/3pl",
    cta: "查看 3PL WMS",
    ctaEn: "View 3PL WMS",
    icon: "ri-box-3-line",
    image: "/tplsp_banner_2000x716.png",
    accent: "text-sky-600",
    bg: "bg-sky-50",
  },
  {
    title: "TMS 運輸系統",
    titleEn: "TMS Transport System",
    eyebrow: "排線 / 車隊 / POD",
    eyebrowEn: "Routing / fleet / POD",
    description:
      "由落單、排線、派車、司機 App、即時追蹤到簽收記錄，建立清楚配送流程。",
    descriptionEn:
      "Manage orders, routing, dispatch, driver app, tracking, and POD in one delivery workflow.",
    href: "/tms",
    cta: "查看 TMS",
    ctaEn: "View TMS",
    icon: "ri-truck-line",
    image: "/tms-main.jpg",
    accent: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    title: "ERP 企業管理系統",
    titleEn: "ERP System",
    eyebrow: "簡單易用 / 逐步上線",
    eyebrowEn: "Simple / modular rollout",
    description:
      "整合財務、採購、銷售、庫存、客戶與報表，適合由 Excel 過渡的香港企業。",
    descriptionEn:
      "Connect finance, purchasing, sales, inventory, CRM, and reporting for companies moving beyond spreadsheets.",
    href: "/erp",
    cta: "查看 ERP",
    ctaEn: "View ERP",
    icon: "ri-building-4-line",
    image: "/dashboard-autochain.png",
    accent: "text-orange-600",
    bg: "bg-orange-50",
  },
] as const;

const scenarioRoutes = [
  {
    title: "網店出貨開始亂",
    titleEn: "Online orders are getting messy",
    description: "多平台訂單、SKU 變體、庫存同步與出貨回寫開始靠人手補救。",
    descriptionEn:
      "Multi-channel orders, SKU variants, inventory sync, and shipment updates need a clearer flow.",
    href: "/wms/ecommerce",
    label: "電商 WMS",
    labelEn: "E-commerce WMS",
    icon: "ri-store-2-line",
    accent: "text-teal-700",
    iconBg: "bg-teal-50",
  },
  {
    title: "同倉要管多個客戶",
    titleEn: "One warehouse serves many clients",
    description: "不同貨主有不同 SOP、文件、收費規則，月底對帳開始吃力。",
    descriptionEn:
      "Different clients, SOPs, documents, and billing rules are making daily operations harder.",
    href: "/wms/3pl",
    label: "3PL WMS",
    labelEn: "3PL WMS",
    icon: "ri-community-line",
    accent: "text-sky-700",
    iconBg: "bg-sky-50",
  },
  {
    title: "排車和追車靠 WhatsApp",
    titleEn: "Dispatch relies on WhatsApp",
    description: "每日排線、司機狀態、POD、客戶查詢分散在不同對話和表格。",
    descriptionEn:
      "Routing, driver status, POD, and customer updates are scattered across chats and sheets.",
    href: "/tms",
    label: "TMS",
    labelEn: "TMS",
    icon: "ri-route-line",
    accent: "text-emerald-700",
    iconBg: "bg-emerald-50",
  },
  {
    title: "採購銷售財務分散",
    titleEn: "Sales, purchasing, and finance are split",
    description:
      "Excel 開始對不上庫存、收款、採購與客戶資料，需要簡單 ERP 接住。",
    descriptionEn:
      "Spreadsheets no longer align stock, collections, purchasing, and customer records.",
    href: "/erp",
    label: "ERP",
    labelEn: "ERP",
    icon: "ri-building-4-line",
    accent: "text-orange-700",
    iconBg: "bg-orange-50",
  },
] as const;

const operatingFlow = [
  {
    title: "1. 集中接單",
    titleEn: "1. Centralize orders",
    system: "訂單入口",
    systemEn: "Order hub",
    description: "把平台、客戶、銷售訂單變成可執行任務。",
    descriptionEn:
      "Turn platform, client, and sales orders into executable work.",
    icon: "ri-inbox-archive-line",
    card: "border-cyan-200 bg-cyan-50",
    iconStyle: "bg-cyan-500 text-white",
  },
  {
    title: "2. 倉庫處理",
    titleEn: "2. Run warehouse work",
    system: "WMS",
    systemEn: "WMS",
    description: "入庫、上架、揀貨、包裝、出貨狀態不再靠人手追。",
    descriptionEn:
      "Inbound, shelving, picking, packing, and shipping status stay visible.",
    icon: "ri-box-3-line",
    card: "border-teal-200 bg-teal-50",
    iconStyle: "bg-teal-600 text-white",
  },
  {
    title: "3. 配送履約",
    titleEn: "3. Fulfill delivery",
    system: "TMS",
    systemEn: "TMS",
    description: "排線、派車、司機 App、POD 回傳形成閉環。",
    descriptionEn:
      "Routing, dispatch, driver app, and POD create a closed loop.",
    icon: "ri-truck-line",
    card: "border-emerald-200 bg-emerald-50",
    iconStyle: "bg-emerald-600 text-white",
  },
  {
    title: "4. 管理對數",
    titleEn: "4. Reconcile management data",
    system: "ERP",
    systemEn: "ERP",
    description: "ERP 接住採購、銷售、庫存、財務與報表。",
    descriptionEn:
      "ERP connects purchasing, sales, inventory, finance, and reporting.",
    icon: "ri-file-chart-line",
    card: "border-orange-200 bg-orange-50",
    iconStyle: "bg-orange-500 text-white",
  },
] as const;

const differences = [
  {
    title: "不是一次過推翻現有流程",
    titleEn: "No big-bang rebuild",
    description: "先處理最痛的一段，例如出貨、排車或對帳，再逐步擴展。",
    descriptionEn:
      "Start with the most painful workflow, then expand in stages.",
    icon: "ri-route-line",
  },
  {
    title: "簡單易用比功能堆疊更重要",
    titleEn: "Ease of use over feature overload",
    description: "系統要讓前線同事肯用，管理層才會得到可信數據。",
    descriptionEn:
      "Frontline adoption comes first; reliable management data follows.",
    icon: "ri-user-smile-line",
  },
  {
    title: "香港團隊，廣東話落地支援",
    titleEn: "Hong Kong team, Cantonese support",
    description: "由需求梳理、培訓到上線後調整，都按本地營運習慣處理。",
    descriptionEn:
      "Requirements, training, and post-launch tuning match local operations.",
    icon: "ri-customer-service-2-line",
  },
  {
    title: "可串接你已經在用的工具",
    titleEn: "Connect what you already use",
    description: "支援 API、電商平台、快遞、客戶系統與內部報表流程。",
    descriptionEn:
      "Connect APIs, stores, couriers, client systems, and reporting flows.",
    icon: "ri-plug-line",
  },
] as const;

const rolloutSteps = [
  {
    title: "先找最痛的一段",
    titleEn: "Start with the pain point",
    description:
      "不是一開始導入所有模組，而是先判斷最影響營運的是庫存、排車、出貨、對帳還是銷售採購。",
    descriptionEn:
      "Do not launch every module at once. Identify whether inventory, dispatch, shipping, reconciliation, or sales and purchasing creates the biggest drag.",
    icon: "ri-search-eye-line",
  },
  {
    title: "用一個流程先跑順",
    titleEn: "Stabilize one workflow",
    description:
      "先讓前線同事用得起來，例如一個倉、一組車線、一個貨主或一個部門，確保流程真正落地。",
    descriptionEn:
      "Start with one warehouse, route group, client, or department so frontline users can adopt the workflow properly.",
    icon: "ri-list-check-2",
  },
  {
    title: "再把資料接起來",
    titleEn: "Connect the data next",
    description:
      "當流程穩定後，再接 API、平台、快遞、財務或客戶系統，避免一開始就被複雜整合拖慢。",
    descriptionEn:
      "Once the workflow is stable, connect APIs, platforms, couriers, finance, or client systems without slowing the first launch.",
    icon: "ri-links-line",
  },
] as const;

const successExperiences = [
  {
    title: "電商及零售履行",
    titleEn: "E-commerce and retail fulfillment",
    description:
      "處理多平台訂單、SKU 變體、庫存同步、揀貨包裝和快遞出貨回寫，讓營運不再依賴人手補表。",
    descriptionEn:
      "Handle multi-channel orders, SKU variants, inventory sync, pick-pack, and courier updates without manual spreadsheet patching.",
    outcome: "由訂單到出貨狀態更清楚",
    outcomeEn: "Clearer order-to-shipment status",
    icon: "ri-shopping-bag-3-line",
  },
  {
    title: "第三方物流倉",
    titleEn: "3PL warehouse operations",
    description:
      "支援多貨主、多 SOP、增值服務、貨主查詢和月底對帳，適合需要把客戶流程分開管理的倉庫。",
    descriptionEn:
      "Support multiple clients, SOPs, value-added services, client enquiries, and month-end reconciliation for 3PL warehouses.",
    outcome: "多貨主流程更易追蹤",
    outcomeEn: "Easier multi-client tracking",
    icon: "ri-community-line",
  },
  {
    title: "本地車隊配送",
    titleEn: "Local fleet delivery",
    description:
      "把落單、排線、派車、司機 App、即時追蹤和 POD 簽收接起來，減少 WhatsApp 追車和追相。",
    descriptionEn:
      "Connect orders, routing, dispatch, driver app, tracking, and POD to reduce delivery chasing through chat.",
    outcome: "配送狀態和 POD 更集中",
    outcomeEn: "Centralized delivery status and POD",
    icon: "ri-truck-line",
  },
  {
    title: "企業管理及對數",
    titleEn: "Business management and reconciliation",
    description:
      "協助由 Excel 過渡到更清晰的採購、銷售、庫存、客戶和財務流程，讓 ERP 更容易被前線接受。",
    descriptionEn:
      "Help teams move from spreadsheets to clearer purchasing, sales, inventory, customer, and finance workflows.",
    outcome: "ERP 由簡單模組開始落地",
    outcomeEn: "ERP starts from practical modules",
    icon: "ri-building-4-line",
  },
] as const;

const proofStats = [
  {
    value: "3 套系統",
    valueEn: "3 layers",
    label: "ERP + 倉存 + 車隊管理",
    labelEn: "ERP + warehouse + transport",
  },
  {
    value: "本地",
    valueEn: "Local",
    label: "香港團隊及廣東話支援",
    labelEn: "Hong Kong team and Cantonese support",
  },
  {
    value: "逐步",
    valueEn: "Modular",
    label: "可先上最急需的模組",
    labelEn: "Start with the module you need most",
  },
  {
    value: "可串接",
    valueEn: "Connected",
    label: "API、平台、快遞與客戶系統",
    labelEn: "APIs, platforms, couriers, client systems",
  },
] as const;

const partners = [
  {
    name: "Casetify",
    logo: "/casetify-logo.png",
    href: "https://www.casetify.com/",
    className: "h-12",
  },
  {
    name: "龍豐集團",
    logo: "/lung-fung-logo.png",
    href: "https://www.lungfung.com.hk/",
    className: "h-20",
  },
  {
    name: "SHIRO",
    logo: "/shiro-logo.png",
    href: "https://shiro-shiro.jp/ec/",
    className: "h-10",
  },
  {
    name: "南順集團",
    logo: "/logo-lamsoon.webp",
    href: "https://www.lamsoon.com/",
    className: "h-24",
  },
  {
    name: "奇華餅家",
    logo: "./keewah-logo.png",
    href: "https://keewah.com/hk/",
    className: "h-12",
  },
  {
    name: "GOGOX",
    logo: "./gogox-logo.png",
    href: "https://www.gogox.com/hk",
    className: "h-12",
  },
] as const;

export default function Home() {
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";

  const text = (zh: string, en: string) => (isZh ? zh : en);

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(text(zhMessage, enMessage));
    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="香港 WMS、TMS、ERP 系統供應商 | AUTOCHAIN"
        description="AUTOCHAIN 是香港專業 WMS、TMS 及 ERP 系統供應商。專為中小企設計，支援倉庫管理、車隊調度、企業管理、財務採購銷售整合。本地團隊、廣東話支援、免費演示。"
        keywords="WMS Vendor, TMS Vendor, ERP 系統, 企業管理系統, WMS, 倉庫管理系統, TMS, 運輸管理系統, 香港WMS, 香港TMS, 香港ERP, 倉儲系統, 物流系統, AUTOCHAIN"
        canonicalUrl="/"
      />

      <Header />

      <main>
        <section
          className="relative flex min-h-[78vh] items-center overflow-hidden bg-cover bg-center text-white md:min-h-[82vh]"
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(5,10,18,0.92) 0%, rgba(5,10,18,0.76) 46%, rgba(5,10,18,0.42) 76%, rgba(5,10,18,0.18) 100%), linear-gradient(0deg, rgba(5,10,18,0.42), rgba(5,10,18,0.04)), url('/3pl-hero-autochain-photo.png')",
          }}
        >
          <div className="relative w-full">
            <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-24 lg:px-8">
              <div className="max-w-4xl">
                <div className="inline-flex items-center gap-2 border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-cyan-100 backdrop-blur">
                  <i className="ri-links-line text-cyan-300"></i>
                  {text(
                    "WMS + TMS + ERP 一體化營運系統",
                    "WMS + TMS + ERP operating systems",
                  )}
                </div>
                <h1 className="mt-6 text-4xl font-bold leading-tight md:text-6xl">
                  {text(
                    "把訂單、倉庫、車隊和企業管理接成一條線",
                    "Connect orders, warehouse, fleet, and business management into one flow",
                  )}
                </h1>
                <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-100 md:text-xl">
                  {text(
                    "AUTOCHAIN 幫香港企業由最混亂的一段流程開始數碼化，再逐步接上 WMS、TMS 和簡單易用 ERP，減少 Excel、WhatsApp 和人手搬資料。",
                    "AUTOCHAIN helps Hong Kong companies digitize the messiest workflow first, then connect WMS, TMS, and simple ERP to reduce spreadsheets, chats, and manual data transfer.",
                  )}
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() =>
                      openWhatsApp(
                        "您好！我想了解 AUTOCHAIN 的 WMS、TMS 及 ERP 方案，希望預約演示。",
                        "Hello! I would like to learn more about AUTOCHAIN's WMS, TMS, and ERP solutions and schedule a demo.",
                      )
                    }
                    className="inline-flex items-center justify-center gap-2 bg-cyan-400 px-6 py-3 font-semibold text-slate-950 transition-colors hover:bg-cyan-300"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    {text("預約方案演示", "Book a demo")}
                  </button>
                  <a
                    href="#solution-finder"
                    className="inline-flex items-center justify-center gap-2 border border-white/40 bg-white/10 px-6 py-3 font-semibold text-white transition-colors hover:bg-white hover:text-slate-950"
                  >
                    {text("找適合方案", "Find the right solution")}
                    <i className="ri-arrow-down-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="solution-finder" className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="text-sm font-semibold uppercase text-teal-700">
                  {text("先判斷問題", "Start with the bottleneck")}
                </span>
                <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-4xl">
                  {text(
                    "不用先選系統，先看哪一段流程最痛",
                    "Do not pick software first. Find the workflow pain first.",
                  )}
                </h2>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {scenarioRoutes.map((scenario) => (
                <Link
                  key={scenario.href}
                  to={scenario.href}
                  className="group border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-slate-400 hover:shadow-lg"
                >
                  <div
                    className={`flex h-12 w-12 items-center justify-center ${scenario.iconBg} ${scenario.accent} text-xl`}
                  >
                    <i className={scenario.icon}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-950">
                    {text(scenario.title, scenario.titleEn)}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {text(scenario.description, scenario.descriptionEn)}
                  </p>
                  <div
                    className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${scenario.accent}`}
                  >
                    {text(scenario.label, scenario.labelEn)}
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 text-white md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                <span className="text-sm font-semibold uppercase text-cyan-300">
                  {text("產品總覽", "Product overview")}
                </span>
                <h2 className="mt-3 text-3xl font-bold md:text-4xl">
                  {text(
                    "四個入口，對應四種營運場景",
                    "Four entry points for four operating scenarios",
                  )}
                </h2>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {solutions.map((solution) => (
                <Link
                  key={solution.href}
                  to={solution.href}
                  className="group overflow-hidden border border-white/10 bg-white/5 transition-all hover:-translate-y-1 hover:bg-white hover:text-slate-950"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-800">
                    <img
                      src={solution.image}
                      alt={text(solution.title, solution.titleEn)}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div
                      className={`inline-flex h-10 w-10 items-center justify-center ${solution.bg} ${solution.accent}`}
                    >
                      <i className={`${solution.icon} text-xl`}></i>
                    </div>
                    <div className="mt-5 text-sm font-semibold text-cyan-300 group-hover:text-slate-500">
                      {text(solution.eyebrow, solution.eyebrowEn)}
                    </div>
                    <h3 className="mt-2 text-xl font-semibold">
                      {text(solution.title, solution.titleEn)}
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-slate-300 group-hover:text-slate-600">
                      {text(solution.description, solution.descriptionEn)}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 group-hover:text-slate-950">
                      {text(solution.cta, solution.ctaEn)}
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
              <div>
                <span className="text-sm font-semibold uppercase text-teal-700">
                  {text("導入路線", "Rollout path")}
                </span>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                  {text(
                    "不用一次過換走所有系統，先由最值得改善的一段開始",
                    "You do not need to replace everything at once. Start where improvement matters most.",
                  )}
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  {text(
                    "這是 AUTOCHAIN 與傳統大型系統導入最不同的地方：我們會先用實際營運流程定範圍，再逐步把 WMS、TMS、ERP 和外部系統接起來。",
                    "This is where AUTOCHAIN differs from traditional large system rollouts: define scope from real operations first, then connect WMS, TMS, ERP, and external systems step by step.",
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {rolloutSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="grid grid-cols-[auto_1fr] gap-5 border border-slate-200 bg-white p-6"
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex h-12 w-12 items-center justify-center bg-slate-950 text-xl text-white">
                        <i className={step.icon}></i>
                      </div>
                      <div className="mt-3 text-sm font-bold text-teal-700">
                        0{index + 1}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-950">
                        {text(step.title, step.titleEn)}
                      </h3>
                      <p className="mt-3 text-sm leading-6 text-slate-600">
                        {text(step.description, step.descriptionEn)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="max-w-5xl">
                <span className="text-sm font-semibold uppercase text-orange-700">
                  {text("成功落地經驗", "Implementation experience")}
                </span>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                  {text(
                    "我們的經驗來自真實營運場景，不只是系統功能展示",
                    "Our experience comes from real operations, not just software feature demos.",
                  )}
                </h2>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {successExperiences.map((experience) => (
                <div
                  key={experience.title}
                  className="border border-slate-200 bg-slate-50 p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center bg-slate-950 text-xl text-white">
                    <i className={experience.icon}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-950">
                    {text(experience.title, experience.titleEn)}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {text(experience.description, experience.descriptionEn)}
                  </p>
                  <div className="mt-5 border-t border-slate-200 pt-4 text-sm font-semibold text-teal-700">
                    {text(experience.outcome, experience.outcomeEn)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div>
                <span className="text-sm font-semibold uppercase text-orange-700">
                  {text("營運流程", "Operating flow")}
                </span>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-950 md:text-4xl">
                  {text(
                    "AUTOCHAIN 不是幾張獨立功能表，而是一條由落單到對數的流程",
                    "AUTOCHAIN is not a stack of feature lists. It is a flow from order to reconciliation.",
                  )}
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  {text(
                    "WMS、TMS 和 ERP 各自處理不同工作，但資料要能流動。這樣前線不用重複輸入，管理層也不用等月底才知道問題。",
                    "WMS, TMS, and ERP handle different jobs, but the data needs to move. Frontline teams avoid duplicate entry, and management sees issues before month end.",
                  )}
                </p>
              </div>

              <div className="relative overflow-hidden border border-slate-200 bg-slate-950 p-5 shadow-xl shadow-slate-200/70 md:p-7">
                <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl"></div>
                <div className="absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-orange-400/20 blur-3xl"></div>

                <div className="relative flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-wide text-cyan-300">
                      {text("營運流程板", "Operating flow board")}
                    </div>
                    <h3 className="mt-2 max-w-lg text-2xl font-bold leading-tight text-white">
                      {text(
                        "一張圖看清資料如何由訂單流到 ERP",
                        "See how data moves from order to ERP in one board",
                      )}
                    </h3>
                  </div>
                  <div className="inline-flex items-center gap-2 self-start border border-cyan-300/40 bg-cyan-300/10 px-3 py-2 text-sm font-semibold text-cyan-100">
                    <span className="h-2 w-2 rounded-full bg-cyan-300"></span>
                    {text("狀態同步", "Status synced")}
                  </div>
                </div>

                <div className="relative mt-7 space-y-3">
                  {operatingFlow.map((step, index) => (
                    <div
                      key={step.title}
                      className="relative grid grid-cols-1 gap-4 border border-white/10 bg-white/[0.06] p-4 transition-colors hover:bg-white/[0.09] md:grid-cols-[auto_1fr_auto]"
                    >
                      <div className="flex items-center gap-3 md:block">
                        <div
                          className={`relative z-10 flex h-11 w-11 items-center justify-center text-xl ${step.iconStyle}`}
                        >
                          <i className={step.icon}></i>
                        </div>
                        <div className="text-sm font-semibold text-cyan-200 md:mt-3 md:text-center">
                          0{index + 1}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                          {text(step.system, step.systemEn)}
                        </div>
                        <h4 className="mt-1 text-lg font-bold text-white">
                          {text(
                            step.title.replace(/^[0-9]. /, ""),
                            step.titleEn.replace(/^[0-9]. /, ""),
                          )}
                        </h4>
                        <p className="mt-2 text-sm leading-6 text-slate-300">
                          {text(step.description, step.descriptionEn)}
                        </p>
                      </div>

                      <div className="flex items-center md:justify-end">
                        <div className="inline-flex items-center gap-2 border border-white/10 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300">
                          {index < operatingFlow.length - 1
                            ? text("傳到下一步", "To next step")
                            : text("形成報表", "Reports ready")}
                          <i
                            className={
                              index < operatingFlow.length - 1
                                ? "ri-arrow-right-line"
                                : "ri-check-line"
                            }
                          ></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="relative mt-5 border border-cyan-300/20 bg-cyan-300/10 p-4">
                  <div className="text-sm font-semibold text-cyan-100">
                    {text(
                      "重點不是多一個畫面，而是每一步資料都接得上。",
                      "The point is not another screen. It is connected data at every step.",
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 text-white md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl">
              <span className="text-sm font-semibold uppercase text-cyan-300">
                {text("不一樣的重點", "What makes it different")}
              </span>
              <h2 className="mt-3 text-3xl font-bold md:text-4xl">
                {text(
                  "我們主力賣的不是厚重系統，而是容易落地的營運改善",
                  "We do not lead with heavy software. We lead with practical operating improvement.",
                )}
              </h2>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              {differences.map((item) => (
                <div
                  key={item.title}
                  className="border border-white/10 bg-white/5 p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center bg-cyan-400 text-xl text-slate-950">
                    <i className={item.icon}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-semibold">
                    {text(item.title, item.titleEn)}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-300">
                    {text(item.description, item.descriptionEn)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {proofStats.map((stat) => (
                <div key={stat.label} className="border border-slate-200 p-5">
                  <div className="text-2xl font-bold text-slate-950">
                    {text(stat.value, stat.valueEn)}
                  </div>
                  <div className="mt-2 text-sm leading-6 text-slate-600">
                    {text(stat.label, stat.labelEn)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 border-t border-slate-200 pt-10">
              <div className="mb-8 text-center text-sm font-semibold uppercase text-slate-500">
                {text("使用我們系統的企業", "Companies using our system")}
              </div>
              <div className="grid grid-cols-6 items-center gap-8 md:grid-cols-6">
                {partners.map((partner) => (
                  <a
                    key={partner.name}
                    href={partner.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={text(`前往 ${partner.name} 官網`, `Visit ${partner.name}'s website`)}
                    className="flex justify-center"
                  >
                    <img
                      src={partner.logo}
                      alt={partner.name}
                      className={`${partner.className} max-w-[160px] object-contain`}
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-teal-600 to-cyan-600 py-16 text-white md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold md:text-4xl">
              {text(
                "先找出最值得數碼化的一段流程",
                "Start with the workflow most worth digitizing",
              )}
            </h2>
            <p className="mt-5 text-lg leading-8 text-white/90">
              {text(
                "你不需要一次過導入所有系統。我們可以先按你的業務模式，判斷應該由 WMS、TMS 還是 ERP 開始。",
                "You do not need to launch every system at once. We can help decide whether WMS, TMS, or ERP should come first based on your business model.",
              )}
            </p>
            <button
              onClick={() =>
                openWhatsApp(
                  "您好！我想了解我們公司應該先導入 WMS、TMS 還是 ERP，希望安排一次諮詢。",
                  "Hello! I would like to understand whether our company should start with WMS, TMS, or ERP. Could we arrange a consultation?",
                )
              }
              className="mt-8 inline-flex items-center justify-center gap-2 bg-white px-7 py-3 font-semibold text-teal-700 transition-colors hover:bg-slate-100"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              {text("WhatsApp 查詢", "Contact on WhatsApp")}
            </button>
          </div>
        </section>

        <section className="border-t bg-white py-14">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none text-slate-700">
              <h2 className="text-2xl font-bold text-slate-950">
                {text(
                  "香港 WMS、TMS 及 ERP 系統供應商",
                  "Hong Kong WMS, TMS & ERP system provider",
                )}
              </h2>
              <p>
                {text(
                  "AUTOCHAIN 為香港企業提供 WMS 倉庫管理系統、TMS 運輸管理系統及 ERP 企業管理系統，覆蓋電商履行、第三方物流、車隊配送、財務採購銷售整合與管理報表。",
                  "AUTOCHAIN provides WMS, TMS, and ERP systems for Hong Kong companies, covering e-commerce fulfillment, 3PL operations, fleet dispatch, finance, purchasing, sales, and management reporting.",
                )}
              </p>
              <p>
                {text(
                  "我們重視簡單易用、本地支援和逐步落地，適合希望由 Excel、WhatsApp 和分散工具過渡到清晰流程的企業。",
                  "We focus on simple adoption, local support, and phased rollout for companies moving beyond spreadsheets, chats, and fragmented tools.",
                )}
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
